import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { FilterIcon } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import {
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import { MembersTable } from "@/components/tables/members";
import { ImportedMembersTable } from "@/components/tables/members-imported";
import { PendingMembersTable } from "@/components/tables/members-pending";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MemberFilterDrawer } from "@/dialogs/member-filter-drawer";
import { env } from "@/env";
import { MemberFilters } from "@/utils/member-filters";
import type { ActiveMembersQuery } from "./__generated__/ActiveMembersQuery.graphql";
import type { ImportedEmailsQuery } from "./__generated__/ImportedEmailsQuery.graphql";
import type { membros_ActiveMembers$key } from "./__generated__/membros_ActiveMembers.graphql";
import type { membros_BatchJobs$key } from "./__generated__/membros_BatchJobs.graphql";
import type { membros_PendingEmail$key } from "./__generated__/membros_PendingEmail.graphql";
import type { membros_PendingMembers$key } from "./__generated__/membros_PendingMembers.graphql";
import type { membrosBatchJobsQuery } from "./__generated__/membrosBatchJobsQuery.graphql";
import type { membrosChangeBatchAlertStatusMutation } from "./__generated__/membrosChangeBatchAlertStatusMutation.graphql";
import type { membrosQuery } from "./__generated__/membrosQuery.graphql";
import type { PendingMembersQuery } from "./__generated__/PendingMembersQuery.graphql";

const defaultValues = {
	view: "ativos",
	page: 1,
	filters: undefined,
} as const;

// JSON schema for json-rules-engine filters
const jsonRulesEngineSchema = z
	.object({
		any: z.array(
			z.object({
				all: z.array(
					z.object({
						fact: z.string(),
						operator: z.string(),
						value: z.string(),
					}),
				),
			}),
		),
	})
	.optional();

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/audiencia/membros/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["ativos", "pendentes", "importados"]).default("ativos"),
			page: z.number().min(1).default(1),
			filters: jsonRulesEngineSchema,
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

const PAGE_SIZE = 15;

function ActiveMembers({
	club,
	onFilteredCountChange,
}: {
	club: membros_ActiveMembers$key;
	onFilteredCountChange?: (count: number) => void;
}) {
	const { page, filters } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const [_, startTransitionActionUpdate] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });

	const [members, refetch] = useRefetchableFragment<
		ActiveMembersQuery,
		membros_ActiveMembers$key
	>(
		graphql`
		fragment membros_ActiveMembers on Club
			@refetchable(queryName: "ActiveMembersQuery")
			@argumentDefinitions(
				first: { type: "Int"}
				last: { type: "Int"}
				after: { type: "String"}
				before: { type: "String"}
				filters: { type: "JSON" }
			)
		{
			clubMembers(first: $first, after: $after, before: $before, last: $last, filters: $filters) {
				edges {
					node {
						id
						user {
							id
							username
							displayName
							email
							avatarFallback
							photo {
								signedUrl
							}
						}
						lastVisit
						visits
						points
						isAdmin
						isModerator
						joinedAt
					}
					cursor
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
			}
		}
	`,
		club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					after:
						page === 1 ? undefined : offsetToCursor((page - 1) * PAGE_SIZE - 1),
					filters: filters ?? null,
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, filters, refetch]);

	const filteredTotalCount = members?.clubMembers?.pageInfo.totalCount || 0;

	useEffect(() => {
		onFilteredCountChange?.(filteredTotalCount);
	}, [filteredTotalCount, onFilteredCountChange]);

	const edgeCount = members?.clubMembers?.edges.length || 0;
	const startIndex = edgeCount > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
	const endIndex = (page - 1) * PAGE_SIZE + edgeCount;

	return (
		<MembersTable
			data={
				members?.clubMembers?.edges.filter(Boolean).map(({ node }) => node) ||
				[]
			}
			isPending={isPending}
			totalCount={members?.clubMembers?.pageInfo.totalCount || 0}
			hasNextPage={Boolean(members?.clubMembers?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(members?.clubMembers?.pageInfo.hasPreviousPage)}
			loadNext={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
				})
			}
			loadPrevious={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
				})
			}
			startIndex={startIndex}
			endIndex={endIndex}
			refetch={() =>
				startTransitionActionUpdate(() => {
					refetch(
						{
							after:
								page === 1
									? undefined
									: offsetToCursor((page - 1) * PAGE_SIZE - 1),
							filters: filters ?? null,
						},
						{ fetchPolicy: "network-only" },
					);
				})
			}
		/>
	);
}

function PendingMembers({ club }: { club: membros_PendingMembers$key }) {
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });

	const [invites, refetch] = useRefetchableFragment<
		PendingMembersQuery,
		membros_PendingMembers$key
	>(
		graphql`
		fragment membros_PendingMembers on Club
			@refetchable(queryName: "PendingMembersQuery")
			@argumentDefinitions(
				first: { type: "Int"}
				last: { type: "Int"}
				after: { type: "String"}
				before: { type: "String"}
				pendingMembersFilter: { type: "JSON!" }
			)
		{
			pendingInvites(
				first: $first
				after: $after
				before: $before
				last: $last
				filters: $pendingMembersFilter
			) {
				edges {
					node {
						id
						email
						type
						url
						createdAt
						seenAt
					}
					cursor
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
			}
		}
	`,
		club,
	);

	const memberInvites = invites?.pendingInvites?.edges
		.filter((edge) => Boolean(edge?.node))
		.filter(({ node }) => node?.type === "MEMBER");

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	return (
		<PendingMembersTable
			onRemoved={() =>
				startTransition(() => {
					refetch(
						{
							after:
								page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
						},
						{ fetchPolicy: "network-only" },
					);
				})
			}
			data={
				memberInvites?.map(({ node }) => ({
					id: node.id,
					email: node.email,
					url: node.url,
					createdAt: node.createdAt,
					seenAt: node.seenAt,
				})) || []
			}
			isPending={isPending}
			totalCount={invites?.pendingInvites?.pageInfo.totalCount || 0}
			hasNextPage={Boolean(invites?.pendingInvites?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(
				invites?.pendingInvites?.pageInfo.hasPreviousPage,
			)}
			loadNext={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
				})
			}
			loadPrevious={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
				})
			}
			startIndex={
				memberInvites?.[0] ? cursorToOffset(memberInvites[0].cursor) + 1 : 0
			}
			endIndex={
				memberInvites?.length
					? cursorToOffset(memberInvites[memberInvites.length - 1].cursor) + 1
					: 0
			}
		/>
	);
}

function ImportedMembers({ club }: { club: membros_PendingEmail$key }) {
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });

	const [emails, refetch] = useRefetchableFragment<
		ImportedEmailsQuery,
		membros_PendingEmail$key
	>(
		graphql`
		fragment membros_PendingEmail on Club
			@refetchable(queryName: "ImportedEmailsQuery")
			@argumentDefinitions(
				first: { type: "Int"}
				last: { type: "Int"}
				after: { type: "String"}
				before: { type: "String"}
				importedEmailsFilters: { type: "JSON!" }
			)
		{
			importedEmails: pendingInvites(
				first: $first
				after: $after
				before: $before
				last: $last
				filters: $importedEmailsFilters
			) {
				edges {
					node {
						id
						email
						type
						url
						createdAt
						seenAt
					}
					cursor
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
			}
		}
	`,
		club,
	);

	const memberInvites = emails?.importedEmails?.edges
		.filter((edge) => Boolean(edge?.node))
		.filter(({ node }) => node?.type === "MEMBER");

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	return (
		<ImportedMembersTable
			onRemoved={() =>
				startTransition(() => {
					refetch(
						{
							after:
								page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
						},
						{ fetchPolicy: "network-only" },
					);
				})
			}
			data={
				memberInvites?.map(({ node }) => ({
					id: node.id,
					email: node.email,
					url: node.url,
					createdAt: node.createdAt,
				})) || []
			}
			isPending={isPending}
			totalCount={emails?.importedEmails?.pageInfo.totalCount || 0}
			hasNextPage={Boolean(emails?.importedEmails?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(
				emails?.importedEmails?.pageInfo.hasPreviousPage,
			)}
			loadNext={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
				})
			}
			loadPrevious={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
				})
			}
			startIndex={
				memberInvites?.[0] ? cursorToOffset(memberInvites[0].cursor) + 1 : 0
			}
			endIndex={
				memberInvites?.length
					? cursorToOffset(memberInvites[memberInvites.length - 1].cursor) + 1
					: 0
			}
		/>
	);
}

function MemberImportAlert({ club }: { club: membros_BatchJobs$key }) {
	const [, startTransition] = useTransition();

	const [data, refetch] = useRefetchableFragment<
		membrosBatchJobsQuery,
		membros_BatchJobs$key
	>(
		graphql`
			fragment membros_BatchJobs on Club
				@refetchable(queryName: "membrosBatchJobsQuery")
			{
				memberImportJobs: batchJobs(type: MEMBER_IMPORT, alertStatus: OPEN) {
					id
					rawID
					status
					completedTaskCount
					taskCount
					failedTaskCount
				}
			}
		`,
		club,
	);

	const [changeBatchAlertStatus] =
		useMutation<membrosChangeBatchAlertStatusMutation>(
			graphql`
				mutation membrosChangeBatchAlertStatusMutation(
					$jobId: String!
					$isClosed: Boolean!
				) {
					changeBatchAlertStatus(jobId: $jobId, isClosed: $isClosed) {
						id
						alertClosed
					}
				}
			`,
		);

	const jobs = data.memberImportJobs;
	const hasPendingJobs = jobs.some((job) => job.status === "PENDING");

	useEffect(() => {
		if (!hasPendingJobs) return;
		const interval = setInterval(() => {
			startTransition(() => {
				refetch({}, { fetchPolicy: "network-only" });
			});
		}, 10000);
		return () => clearInterval(interval);
	}, [hasPendingJobs, refetch]);

	const handleDismiss = (jobId: string) => {
		changeBatchAlertStatus({
			variables: { jobId, isClosed: true },
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				startTransition(() => {
					refetch({}, { fetchPolicy: "network-only" });
				});
			},
			onError: () => {
				toast.error("Erro ao dispensar a notificação. Tente novamente.");
			},
		});
	};

	if (jobs.length === 0) return null;

	return (
		<div className="flex flex-col gap-3 mb-4">
			{jobs.map((job) => {
				if (job.status === "PENDING") {
					const progressPercent =
						job.taskCount > 0
							? Math.round((job.completedTaskCount / job.taskCount) * 100)
							: 0;
					return (
						<Alert
							key={job.id}
							className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
						>
							<Spinner className="size-4 text-blue-600 dark:text-blue-400" />
							<AlertTitle>Importação de membros em andamento</AlertTitle>
							<AlertDescription className="flex flex-col gap-2">
								<span>
									{job.completedTaskCount} de {job.taskCount} membros importados
									({progressPercent}%)
								</span>
								<Progress value={progressPercent} className="h-1.5" />
							</AlertDescription>
						</Alert>
					);
				}

				if (job.status === "COMPLETED") {
					return (
						<Alert
							key={job.id}
							className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
						>
							<Icon
								name="CircleCheck"
								className="size-4 mt-0.5 text-green-600 dark:text-green-400"
							/>
							<AlertTitle className="flex items-center justify-between">
								<span>Importação concluída</span>
								<Button
									variant="ghost"
									size="icon"
									className="size-6"
									onClick={() => handleDismiss(job.rawID)}
									aria-label="Dispensar notificação de importação concluída"
								>
									<Icon name="X" className="size-3.5" />
								</Button>
							</AlertTitle>
							<AlertDescription>
								A importação de membros foi concluída com sucesso.
							</AlertDescription>
						</Alert>
					);
				}

				if (job.status === "FAILED") {
					return (
						<Alert key={job.id} variant="destructive">
							<Icon name="CircleX" className="size-4" />
							<AlertTitle className="flex items-center justify-between">
								<span>Falha na importação</span>
								<Button
									variant="ghost"
									size="icon"
									className="size-6 -mt-1 -mr-2"
									onClick={() => handleDismiss(job.rawID)}
									aria-label="Dispensar notificação de falha na importação"
								>
									<Icon name="X" className="size-3.5" />
								</Button>
							</AlertTitle>
							<AlertDescription>
								{`A importação de membros falhou. ${job.failedTaskCount} de ${job.taskCount} itens falharam.`}
							</AlertDescription>
						</Alert>
					);
				}

				return null;
			})}
		</div>
	);
}

function RouteComponent() {
	const { session } = useRouteContext({ from: "__root__" });
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { view, filters } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [filteredCount, setFilteredCount] = useState<number | null>(null);
	const [isExporting, setIsExporting] = useState(false);

	const data = useLazyLoadQuery<membrosQuery>(
		graphql`
		query membrosQuery(
			$slug: String!
			$first: Int
			$last: Int
			$after: String
			$before: String
			$importedEmailsFilters: JSON!
			$pendingMembersFilter: JSON!
			$activeMembersFilters: JSON
		) {
			club(slug: $slug) {
				id
				members(first: $first, after: $after, before: $before, last: $last) {
				  pageInfo {
					  totalCount
				  }
				}
				pendingInvites(
					first: $first
					after: $after
					before: $before
					last: $last
					filters: $pendingMembersFilter
				){
				  pageInfo {
					  totalCount
				  }
				}
				importedEmails: pendingInvites(
					first: $first
					after: $after
					before: $before
					last: $last
					filters: $importedEmailsFilters
				){
				  pageInfo {
					  totalCount
				  }
				}

				# Filter options
				courses(first: 10000) {
					edges {
						node {
							id
							rawID
							name
						}
					}
				}
				offers(first: 10000) {
					edges {
						node {
							id
							rawID
							productName
						}
					}
				}
				badges(first: 10000) {
					edges {
						node {
							id
							rawID
							...memberBadgeFragment
						}
					}
				}
				groups {
					id
					spaces {
						id
						rawID
						name
						icon
					}
				}
				tags(first: 10000) {
					edges {
						node {
							id
							rawID
							text
						}
					}
				}

				...membros_ActiveMembers @arguments(
					first: $first
					last: $last
					after: $after
					before: $before
					filters: $activeMembersFilters
				)
				...membros_PendingMembers @arguments(
					first: $first
					last: $last
					after: $after
					before: $before
					pendingMembersFilter: $pendingMembersFilter
				)
				...membros_PendingEmail @arguments(
					first: $first
					last: $last
					after: $after
					before: $before
					importedEmailsFilters: $importedEmailsFilters
				)
				...membros_BatchJobs
			}
		}
		`,
		{
			slug: club?.slug,
			first: PAGE_SIZE,
			after: undefined,
			activeMembersFilters: filters ?? null,
			importedEmailsFilters: {
				any: [
					{
						all: [
							{
								fact: "emailSent",
								operator: "nullable",
								value: "",
							},
						],
					},
				],
			},
			pendingMembersFilter: {
				any: [
					{
						all: [
							{
								fact: "emailSent",
								operator: "not_nullable",
								value: "",
							},
						],
					},
				],
			},
		},
	);

	// Prepare filter options
	const courses =
		data.club.courses?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			name: edge.node.name,
		})) || [];

	const externalProducts =
		data.club.offers?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			productName: edge.node.productName,
		})) || [];

	const badges =
		data.club.badges?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			badge: edge.node,
		})) || [];

	const spaces =
		data.club.groups?.filter(Boolean).flatMap((group) =>
			group.spaces.filter(Boolean).map((space) => ({
				rawID: space.rawID,
				name: space.name,
				icon: space.icon ?? undefined,
			})),
		) || [];

	const tags =
		data.club.tags?.edges?.map((edge) => ({
			rawID: edge.node.rawID,
			text: edge.node.text,
		})) || [];

	const filterCategories = MemberFilters({
		courses,
		externalProducts,
		badges,
		spaces,
		tags,
	});

	const handleApplyFilters = (
		newFilters:
			| {
					any: { all: { fact: string; operator: string; value: string }[] }[];
			  }
			| undefined,
	) => {
		navigate({
			search: (s) => ({
				...s,
				page: 1,
				filters: newFilters,
			}),
		});
	};

	const activeFilterCount = filters?.any?.reduce(
		(acc, group) => acc + group.all.length,
		0,
	);

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const headers: HeadersInit = {
				Authorization: `Bearer ${session?.accessToken}`,
			};

			let response: Response;

			if (view === "pendentes") {
				response = await fetch(
					`${env.VITE_SERVER_URL}/${club.slug}/export/invites`,
					{ headers },
				);
			} else if (filters) {
				response = await fetch(
					`${env.VITE_SERVER_URL}/${club.slug}/export/members/filters`,
					{
						method: "POST",
						headers: {
							...headers,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({ filters }),
					},
				);
			} else {
				response = await fetch(
					`${env.VITE_SERVER_URL}/${club.slug}/export/members`,
					{ headers },
				);
			}

			if (!response.ok) {
				throw new Error("Export failed");
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute(
				"download",
				view === "pendentes" ? "convites.xlsx" : "membros.xlsx",
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			toast.error("Erro ao exportar. Tente novamente.");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className="p-6">
			<MemberImportAlert club={data.club} />
			<Tabs
				defaultValue="ativos"
				value={view}
				onValueChange={(val) =>
					navigate({
						search: (s) => ({
							...s,
							view: val as "ativos" | "pendentes" | "importados",
						}),
					})
				}
			>
				<div className="flex flex-row items-center justify-between">
					<TabsList>
						<TabsTrigger value="ativos" className="border-none">
							Ativos (
							{filters && filteredCount !== null
								? `${filteredCount}/${data.club.members.pageInfo.totalCount || 0}`
								: data.club.members.pageInfo.totalCount || 0}
							)
						</TabsTrigger>
						<TabsTrigger value="pendentes" className="border-none">
							Pendentes ({data.club.pendingInvites.pageInfo.totalCount || 0})
						</TabsTrigger>
						<TabsTrigger value="importados" className="border-none">
							Importados ({data.club.importedEmails.pageInfo.totalCount || 0})
						</TabsTrigger>
					</TabsList>

					<div className="flex justify-end items-center gap-2">
						{view === "ativos" && (
							<MemberFilterDrawer
								trigger={
									<Button variant="outline" size="sm" className="gap-2">
										<FilterIcon className="size-4" />
										Filtrar
										{activeFilterCount ? (
											<Badge variant="outline" className="ml-1">
												{activeFilterCount}
											</Badge>
										) : null}
									</Button>
								}
								filterCategories={filterCategories}
								currentFilters={filters}
								onApplyFilters={handleApplyFilters}
							/>
						)}
						<Button
							variant="outline"
							size="sm"
							disabled={view === "importados" || isExporting}
							onClick={handleExport}
						>
							{isExporting && <Spinner />}
							Exportar Visualização
						</Button>
					</div>
				</div>
				<TabsContent value="ativos">
					<ActiveMembers
						club={data.club}
						onFilteredCountChange={setFilteredCount}
					/>
				</TabsContent>
				<TabsContent value="pendentes">
					<PendingMembers club={data.club} />
				</TabsContent>
				<TabsContent value="importados">
					<ImportedMembers club={data.club} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
